import React, { useMemo, useState, useEffect, useCallback } from "react"
import { useDispatch } from "react-redux"
import { useNavigate } from "react-router-dom"
import { Trans, useTranslation } from "react-i18next"
import theme from "lib/material-ui/theme"
import { styled } from "@mui/material/styles"
import Stack from "@mui/material/Stack"
import { BottomSheetAction } from "types/general.types"
import { CLOSE_FRIENDS, CREATE_LIST, EDIT, OWNER } from "constants/index"
import { PrivacyOption, privacyOptions } from "constants/options"
import {
  HANDLE_ADD_ITEM_TO_LIST,
  HANDLE_COPY_LIST_LINK,
  HANDLE_DELETE_LIST,
  HANDLE_EDIT_LIST,
  LIST_MORE_BUTTON_CLICKED,
  NAVIGATE_TO_CREATE_ITEM_PAGE,
  OPEN_LIST_ACTIONS_BOTTOM_SHEET
} from "constants/events"
import { AppDispatch } from "appRedux/store"
import { useAppSelector } from "appRedux/hooks"
import { useDeleteWishListMutation } from "appRedux/slices/wishListApiSlice"
import { updateItemFormFields } from "appRedux/slices/itemFormSlice"
import { updateListFormFields, updateListFormMode } from "appRedux/slices/listFormSlice"
import { updateWishListInfo } from "appRedux/slices/wishListSlice"
import { getPrivacyOptionDetail, handlePremiumOnboarding } from "utils/componentsUtils"
import useEventListener from "hooks/useEventListener"
import useCopyToClipboard from "hooks/useCopyToClipboard"
import useWishListsWithFirstFourItems from "hooks/useWishListsWithFirstFourItems"
import { useAuth } from "context/AuthContext"
import WarningRoundedIcon from "components/atoms/_icons/WarningRoundedIcon"
import Dialog from "components/atoms/Dialog"
import Banner from "components/atoms/Banner"
import WishListActionsBottomSheet from "components/organisms/WishListActionsBottomSheet"
import Lists from "components/organisms/Lists"

type ListsProps = {
  moreButtonActions: BottomSheetAction[];
  totalCount?: number;
}

export default function WishListsList(props: ListsProps) {
  const { moreButtonActions, totalCount = 0 } = props

  const [showListActionsBottomSheet, setShowListActionsBottomSheet] = useState<boolean>(false)
  const [showDeleteListConfirmationDialog, setShowDeleteListConfirmationDialog] = useState<boolean>(false)
  const [clickedList, setClickedList] = useState<WishList | null>(null)

  const formFields = useAppSelector((state) => state.itemForm.formFields)
  const currentPageInfo = useAppSelector((state) => state.global.currentPageInfo)
  const role = useAppSelector((state) => state.global.role)
  const lists = useAppSelector((state) => state.profileTabs.lists)
  const isPremium = useAppSelector((state) => state.user.profile?.isPremium)
  const premiumListCountLimit = useAppSelector((state) => state.initSlice.premiumLimit?.wishlistCount)
  const activePremiumFeatures = useAppSelector((state) => state.premium.activePremiumFeatures)

  const [deleteList, deleteResult] = useDeleteWishListMutation()

  const { copyToClipboard } = useCopyToClipboard()
  const { user } = useAuth()
  const { isFetching: loading } = useWishListsWithFirstFourItems()

  const { t } = useTranslation()
  const dispatch = useDispatch() as AppDispatch
  const navigate = useNavigate()

  const shouldRenderMaxVisibleListsBanner = useCallback(() => {
    if (role === OWNER) return false
    return !loading && totalCount - lists.length > 0
  }, [lists.length, totalCount, loading])

  const shouldRenderProListCountLimitBanner = useCallback(() => {
    if (role !== OWNER) return false
    if (!activePremiumFeatures.hasOwnProperty(CREATE_LIST)) return false
    return !isPremium && !loading && premiumListCountLimit && lists.length >= premiumListCountLimit
  }, [isPremium, lists.length, loading, premiumListCountLimit])

  useEventListener(document, LIST_MORE_BUTTON_CLICKED, handleListMoreButtonClicked)

  useEventListener(document, OPEN_LIST_ACTIONS_BOTTOM_SHEET, handleOpenListActionsBottomSheet)

  useEventListener(document, HANDLE_DELETE_LIST, handleDeleteListButtonClicked)

  useEventListener(document, HANDLE_EDIT_LIST, handleEditListButtonClicked)

  useEventListener(document, HANDLE_ADD_ITEM_TO_LIST, handleAddItemToListButtonClicked)

  useEventListener(document, HANDLE_COPY_LIST_LINK, handleCopyListLink)

  const privacyOptionDetails = useMemo(() => {
    const privacyMap = new Map()

    privacyOptions.forEach((option: PrivacyOption) => {
      if (!privacyMap.has(option.key)) {
        privacyMap.set(option.key, getPrivacyOptionDetail(option.key))
      }
    })

    return privacyMap
  }, [])

  useEffect(() => {
    if (!showListActionsBottomSheet) return
    setShowListActionsBottomSheet(false)
  }, [currentPageInfo?.dynamicPathParts])

  useEffect(() => {
    if (deleteResult.status !== "fulfilled") return
    setShowDeleteListConfirmationDialog(false)
  }, [deleteResult.status])

  function handleMoreButtonClicked(list: WishList) {
    document.dispatchEvent(new CustomEvent(LIST_MORE_BUTTON_CLICKED, { detail: list }))
  }

  function handleListMoreButtonClicked(event: CustomEvent) {
    const list = event.detail
    setClickedList(list)
  }

  function handleOpenListActionsBottomSheet() {
    setShowListActionsBottomSheet(true)
  }

  function handleDeleteListButtonClicked() {
    setShowListActionsBottomSheet(false)
    setShowDeleteListConfirmationDialog(true)
  }

  function handleEditListButtonClicked(event: React.MouseEvent<HTMLButtonElement>) {
    setShowListActionsBottomSheet(false)
    if (!clickedList || !user?.handle) return
    const { id, name, privacy } = clickedList

    if (!isPremium && privacy === CLOSE_FRIENDS) {
      handlePremiumOnboarding(event, privacy, dispatch)
      return
    }

    dispatch(updateListFormMode(EDIT))
    dispatch(updateListFormFields({
      name,
      privacy
    }))
    navigate(`/user/${user?.handle}/list/${id}/edit`)
  }

  function handleAddItemToListButtonClicked(event: React.MouseEvent<HTMLButtonElement>) {
    setShowListActionsBottomSheet(false)
    if (!clickedList) return

    const { privacy } = clickedList
    if (!isPremium && privacy === CLOSE_FRIENDS) {
      handlePremiumOnboarding(event, privacy, dispatch)
      return
    }

    dispatch(updateWishListInfo(clickedList))
    dispatch(updateItemFormFields({
      ...formFields,
      wishlistIds: [clickedList.id]
    }))
    document.dispatchEvent(new CustomEvent(NAVIGATE_TO_CREATE_ITEM_PAGE))
  }

  async function handleCopyListLink() {
    const pageLink = getListUrl()
    await copyToClipboard(pageLink)
  }

  function getListUrl() {
    if (!currentPageInfo) return ""
    const username = currentPageInfo?.dynamicPathParts?.handle
    return window.location.origin + "/public/user/" + username + "/list/" + clickedList?.id
  }

  function handleCloseListActionsBottomSheet() {
    setShowListActionsBottomSheet(false)
    setClickedList(null)
  }

  function handleCloseDeleteListConfirmationDialog() {
    setShowDeleteListConfirmationDialog(false)
  }

  function handleDeleteItem() {
    if (!clickedList) return
    deleteList(clickedList.id)
  }

  function getActionsBottomSheetTitle() {
    if (!clickedList) return
    return (
      <Stack direction="row" alignItems="center" justifyContent="center" gap={2}>
        <PrivacyIconWrapper>
          {privacyOptionDetails.get(clickedList.privacy)?.filledIcon}
        </PrivacyIconWrapper>
        <ListName>{clickedList.name}</ListName>
      </Stack>
    )
  }

  return (
    <>
      {shouldRenderMaxVisibleListsBanner() && <Banner
          severity="info"
          message={t("collections.visibleListsCount", { count: lists.length })}
          style={{ marginBottom: "16px" }}
      />}
      {shouldRenderProListCountLimitBanner() && <Banner
          severity="warning"
          message={t("premium.upgradeToProForMoreListCreation")}
          isProLimit={true}
          feature={CREATE_LIST}
          style={{ marginBottom: "16px" }}
      />}
      <Lists
        loading={loading}
        lists={lists}
        privacyOptionDetails={privacyOptionDetails}
        handleMoreButtonClicked={handleMoreButtonClicked}
      />
      <WishListActionsBottomSheet
        show={showListActionsBottomSheet}
        title={getActionsBottomSheetTitle()}
        actions={moreButtonActions}
        onOpen={() => {
        }}
        onClose={handleCloseListActionsBottomSheet}
      />
      <Dialog
        message={<Trans
          i18nKey={"collections.deleteConfirmationQuestion"}
          components={{
            strong: <strong style={{
              color: theme.palette.gray["15"],
              fontWeight: 700
            }}/>
          }}
        />}
        icon={<WarningRoundedIcon/>}
        buttons={[
          {
            label: t("general.submit"),
            mode: "white",
            loading: deleteResult.isLoading,
            onClick: handleDeleteItem
          },
          {
            label: t("general.discard"),
            mode: "primary",
            loading: false,
            disabled: deleteResult.isLoading,
            onClick: handleCloseDeleteListConfirmationDialog
          }
        ]}
        open={showDeleteListConfirmationDialog}
        disableBackdropClose={true}
        onClose={handleCloseDeleteListConfirmationDialog}
      />
    </>
  )
}

const PrivacyIconWrapper = styled(Stack)(() => ({
  flexShrink: 0,
  width: 24,
  height: 24,
  justifyContent: "center",
  alignItems: "center"
}))

const ListName = styled(Stack)(({ theme }) => ({
  justifyContent: "center",
  fontSize: "1.6rem",
  color: theme.palette.gray["15"],
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  paddingTop: "2px"
}))
